import { useEffect, useMemo, useRef, type ChangeEvent } from 'react';
import { useQuery } from '@apollo/client/react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleCheck } from '@fortawesome/free-solid-svg-icons';
import { Alert } from '../../../shared/components/alert';
import { Checkbox } from '../../../shared/components/checkbox';
import { FormLabel } from '../../../shared/components/form-label';
import { NumericStepper } from '../../../shared/components/numeric-stepper';
import { TextInput } from '../../../shared/components/text-input';
import { ValidationMessage } from '../../../shared/components/validation-message';
import { EX_WORKS_POSTCODE, matchAreaCode } from '../../../shared/utils/postcode';
import { AREA_CODES_QUERY, type AreaCodesQueryResult } from '../graphql';
import { exceedsFloorArea, MAX_FLOOR_AREA, type WizardFormValues } from '../schemas';
import { StepHeading } from './step-heading';

export function Step1FloorSize() {
  const {
    control,
    register,
    setValue,
    getValues,
    formState: { errors },
  } = useFormContext<WizardFormValues>();
  const { data } = useQuery<AreaCodesQueryResult>(AREA_CODES_QUERY);
  const areaCodes = useMemo(() => data?.areaCodes ?? [], [data]);

  const exWorks = useWatch({ control, name: 'exWorks' });
  const siteAreaCodeId = useWatch({ control, name: 'siteAreaCodeId' });
  const matched = useMemo(() => {
    if (exWorks || siteAreaCodeId == null) return null;
    const row = areaCodes.find((a) => a.id === siteAreaCodeId);
    return row && row.postCode !== EX_WORKS_POSTCODE ? row : null;
  }, [siteAreaCodeId, areaCodes, exWorks]);
  const exWorksRow = useMemo(
    () => areaCodes.find((a) => a.postCode === EX_WORKS_POSTCODE) ?? null,
    [areaCodes],
  );

  // On first load of the area-codes list, reconcile a prefilled `siteAreaCodeId`
  // back to the user-facing `site`/`exWorks` fields, and recover if the user
  // typed before Apollo returned the list. The onChange handlers below keep
  // `siteAreaCodeId` in sync synchronously thereafter.
  const reconciledRef = useRef(false);
  useEffect(() => {
    if (areaCodes.length === 0) return;

    if (!reconciledRef.current) {
      reconciledRef.current = true;
      const prefilledId = getValues('siteAreaCodeId');
      if (prefilledId != null && !getValues('site') && !getValues('exWorks')) {
        const row = areaCodes.find((a) => a.id === prefilledId);
        if (row?.postCode === EX_WORKS_POSTCODE) {
          setValue('exWorks', true, { shouldValidate: true });
        } else if (row) {
          setValue('site', row.postCode, { shouldValidate: true });
        }
        return;
      }
    }

    const desiredId = getValues('exWorks')
      ? (exWorksRow?.id ?? null)
      : (matchAreaCode(getValues('site'), areaCodes)?.id ?? null);
    if (getValues('siteAreaCodeId') !== desiredId) {
      setValue('siteAreaCodeId', desiredId, { shouldValidate: true });
    }
  }, [areaCodes, exWorksRow, getValues, setValue]);

  const siteRegistration = register('site', {
    onChange: (e: ChangeEvent<HTMLInputElement>) => {
      if (getValues('exWorks')) return;
      const m = matchAreaCode(e.target.value, areaCodes);
      setValue('siteAreaCodeId', m?.id ?? null);
    },
  });

  const exWorksRegistration = register('exWorks', {
    onChange: (e: ChangeEvent<HTMLInputElement>) => {
      if (e.target.checked) {
        setValue('siteAreaCodeId', exWorksRow?.id ?? null);
      } else {
        const m = matchAreaCode(getValues('site'), areaCodes);
        setValue('siteAreaCodeId', m?.id ?? null);
      }
    },
  });

  const lengthValue = useWatch({ control, name: 'length' });
  const widthValue = useWatch({ control, name: 'width' });
  const hasAreaError = exceedsFloorArea(Number(lengthValue), Number(widthValue));

  return (
    <div className="space-y-5">
      <StepHeading
        title="Floor size"
        description={
          <>
            Where will the floor go, and how big is it?
          </>
        }
      />

      <div>
        <FormLabel htmlFor="site">Site postcode</FormLabel>
        <TextInput
          id="site"
          autoComplete="postal-code"
          placeholder="e.g. GU32 3QA"
          disabled={!!exWorks}
          {...siteRegistration}
        />
        {matched && !errors.site && (
          <p
            className="mt-1.5 text-xs font-medium text-emerald-600 flex items-center gap-1.5"
            data-testid="site-match"
          >
            <FontAwesomeIcon icon={faCircleCheck} className="text-sm" aria-hidden="true" />
            Delivery area: {matched.postCode} — {matched.area}
          </p>
        )}
        <ValidationMessage error={errors.site as never} message={errors.site?.message ?? ''} />

        <div className="mt-3">
          <Checkbox
            id="exWorks"
            label="I'll collect the goods myself (Ex Works — no delivery)"
            {...exWorksRegistration}
          />
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <FormLabel htmlFor="length">Length</FormLabel>
          <Controller
            control={control}
            name="length"
            render={({ field }) => (
              <NumericStepper
                id="length"
                value={field.value}
                onChange={field.onChange}
                onBlur={field.onBlur}
                min={1}
                suffixLabel="m"
              />
            )}
          />
          <ValidationMessage
            error={errors.length as never}
            message={errors.length?.message ?? ''}
          />
        </div>

        <div>
          <FormLabel htmlFor="width">Width</FormLabel>
          <Controller
            control={control}
            name="width"
            render={({ field }) => (
              <NumericStepper
                id="width"
                value={field.value}
                onChange={field.onChange}
                onBlur={field.onBlur}
                min={1}
                suffixLabel="m"
              />
            )}
          />
          {!hasAreaError && (
            <ValidationMessage
              error={errors.width as never}
              message={errors.width?.message ?? ''}
            />
          )}
        </div>
      </div>

      {hasAreaError && (
        <Alert>
          For budget pricing on floors over {MAX_FLOOR_AREA} m<sup>2</sup> please contact us on{' '}
          <a href="tel:01730237190" className="font-semibold underline">
            01730 237190
          </a>{' '}
          or{' '}
          <a href="mailto:sales@hi-level.co.uk" className="font-semibold underline">
            sales@hi-level.co.uk
          </a>
          .
        </Alert>
      )}
    </div>
  );
}
